import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import { Link } from "react-router";
import { attentionItems } from "../../app/flags";
import { problemMessage } from "../../shared/api/problem";
import { useNeedsAttention, useUserDirectory } from "../../shared/api/queries";
import { attentionWords } from "../../shared/lib/attentionWords";
import { useAnnounce } from "../../shared/components/announcer-context";
import { LoadError } from "../../shared/components/LoadError";
import { Popover } from "../../shared/components/Popover";
import { Switch } from "../../shared/components/Switch";
import { useSession } from "../../shared/session/session-context";
import { PANEL_PAGE_SIZE, type NotificationSurface, rememberedTab, rememberTab, useMarkAllRead, useNotificationList, useSetRead, useUnreadOnly } from "./api";
import { NotificationRow } from "./NotificationRow";
import { notificationText } from "./notificationText";
import { CHANNELS, groupRows, isUnread, tabLabel, type Notification, type NotificationChannel, type NotificationCount } from "./rowModel";


/**
 * One tab's list (design §2.1–§2.3): Latest / Older groups, "Show older" (no infinite scroll; focus lands on the first new
 * row), and every state of §2.2. Shared by the bell panel and (stream N-W2) the full page.
 */
export function NotificationListView({
  channel,
  unreadOnly,
  onShowAll,
  onOpen,
  pageSize = PANEL_PAGE_SIZE,
  excerptLines = 3,
  surface = "panel",
  eventType = null,
  emptyText,
  watchingAnything,
}: {
  channel: NotificationChannel;
  unreadOnly: boolean;
  onShowAll: () => void;
  onOpen: (notification: Notification, sameTab: boolean) => void;
  pageSize?: number;
  excerptLines?: number;
  /** Which surface owns the cached list (the panel drops its lists on close, the full page on a tab or filter change). */
  surface?: NotificationSurface;
  /** Only notices of this event type (the full page's "Mentions only" filter); null = every kind. */
  eventType?: string | null;
  /** Replaces the tab's own empty text (switch off) when a filter narrows the list. */
  emptyText?: string;
  /**
   * Whether the reader watches any item, when known. The contract has no such field yet, so it is undefined and an empty
   * Watching tab says "No activity on the items you watch yet."
   */
  watchingAnything?: boolean;
}) {
  const list = useNotificationList(channel, unreadOnly, true, pageSize, surface, eventType);
  const setRead = useSetRead();
  const announce = useAnnounce();
  const { byId } = useUserDirectory();
  const rows = useMemo(() => list.data?.pages.flatMap((page) => page.items) ?? [], [list.data]);
  const [now] = useState(() => new Date());
  const { latest, older } = groupRows(rows, now);
  const rowRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const focusFrom = useRef<number | null>(null);
  const [readError, setReadError] = useState<string | null>(null);

  // "Show older" keeps focus on the first newly loaded row (design §2.1, AC-P3).
  useEffect(() => {
    const from = focusFrom.current;
    if (from === null || rows.length <= from) return;
    rowRefs.current[from]?.focus();
    focusFrom.current = null;
  }, [rows.length]);

  function open(notification: Notification, sameTab: boolean) {
    // Do not wait: opening the item matters more than the read flag (design §3.4).
    if (isUnread(notification)) setRead.mutate({ id: notification.id, read: true });
    onOpen(notification, sameTab);
  }

  function toggleRead(notification: Notification) {
    const read = isUnread(notification);
    setReadError(null);
    setRead.mutate(
      { id: notification.id, read },
      {
        onSuccess: () => announce(notificationText(read ? "markedRead" : "markedUnread")),
        onError: (error) => setReadError(`${notificationText("notMarked")} ${problemMessage(error)}`),
      },
    );
  }

  const renderRows = (group: Notification[], offset: number) => (
    <ul className="gf-notif-list">
      {group.map((notification, index) => (
        <NotificationRow
          key={notification.id}
          notification={notification}
          directory={byId}
          excerptLines={excerptLines}
          onOpen={open}
          onToggleRead={toggleRead}
          now={now}
          rowRef={(element) => {
            rowRefs.current[offset + index] = element;
          }}
        />
      ))}
    </ul>
  );

  if (list.isPending) {
    return (
      <div className="gf-notif-loading">
        <p role="status" className="vh">
          {notificationText("loading")}
        </p>
        {[0, 1, 2].map((n) => (
          <div key={n} className="gf-notif-skeleton" aria-hidden="true">
            <span className="av" />
            <span className="lines">
              <span />
              <span />
            </span>
          </div>
        ))}
      </div>
    );
  }
  if (list.isError && !list.data) {
    return (
      <div className="gf-notif-state">
        <LoadError error={list.error} lead={notificationText("loadError")} onRetry={() => void list.refetch()} />
      </div>
    );
  }
  if (rows.length === 0) {
    if (unreadOnly) {
      return (
        <div className="gf-notif-state">
          <p>{notificationText("caughtUp")}</p>
          <button type="button" className="btn sec" onClick={onShowAll}>
            {notificationText("showAll")}
          </button>
        </div>
      );
    }
    const text = channel === "direct" ? "emptyDirect" : watchingAnything === false ? "emptyWatchingNone" : "emptyWatchingSome";
    return (
      <div className="gf-notif-state">
        <p>{emptyText ?? notificationText(text)}</p>
      </div>
    );
  }
  return (
    <div className="gf-notif-groups">
      {readError && (
        <div className="msg gf-notif-inline-error" role="alert">
          {readError}
        </div>
      )}
      {latest.length > 0 && (
        <section className="gf-notif-group">
          <h3>{notificationText("latest")}</h3>
          {renderRows(latest, 0)}
        </section>
      )}
      {older.length > 0 && (
        <section className="gf-notif-group">
          <h3>{notificationText("older")}</h3>
          {renderRows(older, latest.length)}
        </section>
      )}
      {list.isFetchNextPageError && (
        <div className="msg gf-notif-inline-error" role="alert">
          <span aria-hidden="true">! </span>
          {notificationText("olderError")} {problemMessage(list.error)}{" "}
          <button type="button" className="linkish" onClick={() => void list.fetchNextPage()}>
            {notificationText("tryAgain")}
          </button>
        </div>
      )}
      {list.hasNextPage && (
        <div className="gf-notif-more">
          <button
            type="button"
            className="btn sec"
            disabled={list.isFetchingNextPage}
            onClick={() => {
              focusFrom.current = rows.length;
              void list.fetchNextPage();
            }}
          >
            {list.isFetchingNextPage ? notificationText("loadingOlder") : notificationText("showOlder")}
          </button>
        </div>
      )}
    </div>
  );
}

/** A plain left click follows the link in this tab; any modifier or another button opens elsewhere and keeps the panel open. */
function followsHere(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey;
}

/**
 * A line's content: the count chip, then the words without repeating a leading number ("(3) deployments awaiting your approval",
 * merge §3.1). The chip IS the number there, so the link's name reads exactly Home's words ("3 deployments awaiting your
 * approval", §4) and matches what is seen (label in name). Words that carry their number elsewhere, or a count shown as 99+,
 * keep the full words and hide the chip from the name.
 */
function AttentionLineContent({ words, count }: { words: string; count: number }) {
  const lead = `${count} `;
  const leads = words.startsWith(lead) && count <= 99;
  return (
    <>
      <span className="gf-notif-attention-count" aria-hidden={leads ? undefined : true}>
        {count > 99 ? "99+" : count}
      </span>{" "}
      <span className="gf-notif-attention-words">{leads ? words.slice(lead.length) : words}</span>
    </>
  );
}

/**
 * The "Needs attention" section pinned above the Direct / Watching tabs (batch 17 attention-bell merge §3; ruling D2 (a)). State,
 * not read/unread: "Only show unread" and "Mark all … as read" never touch it. One line per kind with a count, in the server's
 * order, with Home's words; the heading total is the bell's number. Partners get it too (ruling D4 (b)): their kinds are what
 * the server computes for them.
 */
function AttentionSection({ firstLineRef, onNavigate }: { firstLineRef: (element: HTMLAnchorElement | null) => void; onNavigate: () => void }) {
  const session = useSession();
  const attention = useNeedsAttention(true);
  const headingId = useId();
  const lines = attentionItems(attention.data, session).filter((item) => item.count > 0);
  const total = lines.reduce((sum, item) => sum + item.count, 0);
  const follow = (event: MouseEvent<HTMLAnchorElement>) => {
    if (followsHere(event)) onNavigate();
  };
  return (
    <section className="gf-notif-attention" aria-labelledby={headingId}>
      <div className="gf-notif-attention-head">
        <span className="gf-notif-attention-title">
          <h3 id={headingId}>{notificationText("attentionHeading")}</h3>
          {/* The same total as the bell's chip; each line's name carries its own count, so the chip is not read twice. */}
          {attention.isSuccess && total > 0 && (
            <span className="gf-notif-attention-total" aria-hidden="true" data-testid="attention-total">
              {total > 99 ? "99+" : total}
            </span>
          )}
        </span>
        <Link to="/" className="gf-notif-attention-home" onClick={follow}>
          {notificationText("attentionOpenHome")}
        </Link>
      </div>
      {attention.isPending && (
        <div className="gf-notif-attention-loading">
          <p role="status" className="vh">
            {notificationText("attentionLoading")}
          </p>
          <div className="gf-notif-attention-skeleton" aria-hidden="true">
            <span />
          </div>
        </div>
      )}
      {attention.isError && !attention.data && (
        <div className="gf-notif-attention-error">
          <LoadError error={attention.error} lead={notificationText("attentionLoadError")} onRetry={() => void attention.refetch()} />
        </div>
      )}
      {attention.isSuccess && lines.length === 0 && <p className="gf-notif-attention-empty">{notificationText("attentionNothing")}</p>}
      {lines.length > 0 && (
        <ul className="gf-notif-attention-lines" aria-label={notificationText("attentionLines")}>
          {lines.map((item, index) => (
            <li key={item.kind}>
              <Link to={item.linkPath} ref={index === 0 ? firstLineRef : undefined} onClick={follow}>
                <AttentionLineContent words={attentionWords(item.kind, item.count)} count={item.count} />
                <svg className="gf-notif-attention-chev" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The bell's panel (design §2): a non-modal dialog with the heading, "Only show unread", the full-page link, the ⋮ menu, the
 * Needs-attention section (attention-bell merge §3), the Direct / Watching tabs (sticky once the section scrolls away) and the
 * list. On open, focus lands on the first thing waiting on you: the first attention line, else the active tab (merge §4).
 */
export function NotificationPanel({
  id,
  phone,
  count,
  onClose,
  onNavigate,
}: {
  id: string;
  phone: boolean;
  count: NotificationCount | undefined;
  /** Close and return focus to the bell (Close button, Esc). */
  onClose: () => void;
  /** Close because a row or link is being followed in this tab (focus goes wherever the page puts it). */
  onNavigate: () => void;
}) {
  const session = useSession();
  const userId = session.user.id;
  const announce = useAnnounce();
  const [tab, setTab] = useState<NotificationChannel>(() => rememberedTab(userId));
  const [unreadOnly, setUnreadOnly] = useUnreadOnly(userId);
  const markAll = useMarkAllRead();
  // Batch 18 GRF-57 (UX §10.8–9): with Features__NotificationSettings on, "Mark all read" is a visible title-bar button and the
  // ⋮ menu gives way to a gear link to the settings page. Off: the batch 17 ⋮ menu, unchanged.
  const settingsPage = session.flag("NotificationSettings");
  const markAllDescId = useId();
  const headingId = useId();
  const switchId = useId();
  const labelId = useId();
  const tabsId = useId();
  const tabRefs = useRef<Record<NotificationChannel, HTMLButtonElement | null>>({ direct: null, watching: null });
  const firstLine = useRef<HTMLAnchorElement | null>(null);
  const attention = useNeedsAttention(true);
  const focused = useRef(false);

  // Focus once, when the panel opens (merge §4, AC-M13): the first attention line when there is one, otherwise the active tab.
  // While Needs attention is still loading for the first time, wait for it rather than move focus twice.
  useEffect(() => {
    if (focused.current || attention.isPending) return;
    focused.current = true;
    (firstLine.current ?? tabRefs.current[tab])?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attention.isPending]);

  function choose(next: NotificationChannel, focus: boolean) {
    setTab(next);
    rememberTab(userId, next);
    markAll.reset();
    if (focus) tabRefs.current[next]?.focus();
  }

  function onTabKey(event: KeyboardEvent<HTMLButtonElement>) {
    const index = CHANNELS.indexOf(tab);
    let next: NotificationChannel | undefined;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") next = CHANNELS[(index + 1) % CHANNELS.length];
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = CHANNELS[(index + CHANNELS.length - 1) % CHANNELS.length];
    else if (event.key === "Home") next = CHANNELS[0];
    else if (event.key === "End") next = CHANNELS[CHANNELS.length - 1];
    if (!next) return;
    event.preventDefault();
    choose(next, true);
  }

  function markAllRead(close: () => void) {
    const n = count?.[tab];
    close();
    markAll.mutate(
      { channel: tab, unreadOnly },
      {
        onSuccess: () => {
          if (n === undefined) announce(notificationText("allMarkedTab", { tab: tabLabel(tab) }));
          else announce(n === 1 ? notificationText("allMarkedOne") : notificationText("allMarkedMany", { n }));
        },
      },
    );
  }

  /** GRF-57: the visible button, current tab only; announces with the tab's name and keeps focus where it is. */
  function markAllVisible() {
    if ((count?.[tab] ?? 0) === 0 || markAll.isPending) return;
    markAll.mutate({ channel: tab, unreadOnly }, { onSuccess: () => announce(notificationText("allMarkedNotices", { tab: tabLabel(tab) })) });
  }

  function openRow(_notification: Notification, sameTab: boolean) {
    if (sameTab) onNavigate();
  }

  return (
    <div id={id} role="dialog" aria-labelledby={headingId} className={phone ? "gf-notif-panel sheet" : "gf-notif-panel"}>
      <div className="gf-notif-head">
        <div className="gf-notif-titlebar">
          <h2 id={headingId}>{notificationText("heading")}</h2>
          <div className="gf-notif-tools">
            <span className="gf-notif-switch">
              <label id={labelId} htmlFor={switchId}>
                {notificationText("onlyUnread")}
              </label>
              <Switch id={switchId} checked={unreadOnly} labelledBy={labelId} onChange={setUnreadOnly} />
            </span>
            {settingsPage && (
              <>
                <button
                  type="button"
                  className="btn sec gf-notif-markall"
                  aria-label={notificationText("markAllTabName", { tab: tabLabel(tab) })}
                  aria-disabled={(count?.[tab] ?? 0) === 0 || markAll.isPending}
                  aria-describedby={(count?.[tab] ?? 0) === 0 ? markAllDescId : undefined}
                  onClick={markAllVisible}
                >
                  {notificationText("markAllRead")}
                </button>
                <span id={markAllDescId} hidden>
                  {notificationText("nothingUnread")}
                </span>
              </>
            )}
            <Link className="gf-iconbtn" to={`/notifications?tab=${tab}`} aria-label={notificationText("openFullPage")} title={notificationText("openFullPage")} onClick={onNavigate}>
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <path d="M9.5 2.5h4v4M13.3 2.7 7.5 8.5M12 9.5V13a.5.5 0 0 1-.5.5h-8A.5.5 0 0 1 3 13V5a.5.5 0 0 1 .5-.5H7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            {settingsPage ? (
              <Link className="gf-iconbtn" to="/me/notifications/settings" aria-label={notificationText("settings")} title={notificationText("settings")} onClick={onNavigate}>
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <circle cx="8" cy="8" r="2.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </Link>
            ) : (
              <Popover
                label={notificationText("moreActions")}
                buttonClassName="gf-iconbtn"
                buttonLabel={notificationText("moreActions")}
                button={
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <circle cx="8" cy="3.2" r="1.4" fill="currentColor" />
                    <circle cx="8" cy="8" r="1.4" fill="currentColor" />
                    <circle cx="8" cy="12.8" r="1.4" fill="currentColor" />
                  </svg>
                }
              >
                {(close) => (
                  <ul className="gf-menu">
                    <li>
                      <button type="button" onClick={() => markAllRead(close)} disabled={markAll.isPending}>
                        {notificationText("markAllIn", { tab: tabLabel(tab) })}
                      </button>
                    </li>
                    <li>
                      <Link
                        to="/notifications#settings"
                        onClick={() => {
                          close();
                          onNavigate();
                        }}
                      >
                        {notificationText("settings")}
                      </Link>
                    </li>
                  </ul>
                )}
              </Popover>
            )}
            {phone && (
              <button type="button" className="gf-iconbtn gf-notif-close" onClick={onClose} aria-label={notificationText("close")}>
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
        </div>
        {markAll.isError && (
          <div className="msg gf-notif-inline-error" role="alert">
            {notificationText("markAllError")} {problemMessage(markAll.error)}
          </div>
        )}
      </div>
      <div className="gf-notif-scroll">
        <AttentionSection
          firstLineRef={(element) => {
            firstLine.current = element;
          }}
          onNavigate={onNavigate}
        />
        <div className="gf-notif-tabs gf-notif-tabs-sticky" role="tablist" aria-label={notificationText("tabsLabel")}>
          {CHANNELS.map((channel) => {
            const unread = count?.[channel] ?? 0;
            return (
              <button
                key={channel}
                ref={(element) => {
                  tabRefs.current[channel] = element;
                }}
                type="button"
                role="tab"
                id={`${tabsId}-${channel}`}
                aria-selected={tab === channel}
                aria-controls={`${tabsId}-${channel}-panel`}
                tabIndex={tab === channel ? 0 : -1}
                onClick={() => choose(channel, false)}
                onKeyDown={onTabKey}
              >
                {tabLabel(channel)}
                {unread > 0 && ` (${unread})`}
              </button>
            );
          })}
        </div>
        <div role="tabpanel" id={`${tabsId}-${tab}-panel`} aria-labelledby={`${tabsId}-${tab}`}>
          <NotificationListView key={`${tab}-${unreadOnly}`} channel={tab} unreadOnly={unreadOnly} onShowAll={() => setUnreadOnly(false)} onOpen={openRow} excerptLines={phone ? 2 : 3} />
        </div>
      </div>
    </div>
  );
}
